import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import type { AssetOption, Entity } from '@einhaus/project314-types';
import { Drawer } from '@/components/UI';
import EntityLink from '@/components/Entity/EntityLink';
import type { PerformanceTrade } from '../performanceTypes';
import { formatSignedCurrency, formatSignedPercent, formatUnsignedCurrency, signedToneClass } from '../performanceFormat';
import {
    formatLongDate,
    formatShortDate,
    getDayPnl,
    getExitReasonLabel,
    getInstrumentLabel,
    getScopedStrategyDays,
    getTradeHoldDays,
    type DayDetail,
    type PerformanceBasis,
    type StrategyScope
} from '../performanceUtils';
import StrategyColorDot from './StrategyColorDot';

interface DayDetailDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    detail: DayDetail | null;
    basis: PerformanceBasis;
    scope: StrategyScope;
    strategyColors: Map<number, string>;
    strategyNames: Map<number, string>;
    entitiesById: Map<number, Entity>;
    optionsById: Map<string, AssetOption>;
    today: string;
    portfolioId: number;
}

const Row = ({ label, value, emphasized = false }: { label: string; value: ReactNode; emphasized?: boolean }) => (
    <div className={`flex items-baseline justify-between gap-3 text-sm ${emphasized ? 'font-semibold' : ''}`}>
        <span className="text-zinc-500">{label}</span>
        <span className="text-right text-zinc-200">{value}</span>
    </div>
);

const SectionTitle = ({ children }: { children: ReactNode }) => (
    <h4 className="mb-2 text-xs uppercase tracking-wider text-zinc-400">{children}</h4>
);

const DayDetailDrawer = ({
    isOpen,
    onClose,
    detail,
    basis,
    scope,
    strategyColors,
    strategyNames,
    entitiesById,
    optionsById,
    today,
    portfolioId
}: DayDetailDrawerProps) => {
    const renderTrade = (trade: PerformanceTrade, variant: 'closed' | 'opened' | 'held') => {
        const entity = entitiesById.get(trade.entityId);
        const option =
            trade.derivativeId !== null ? optionsById.get(`${trade.entityType}:${trade.entityId}:${trade.derivativeId}`) : undefined;
        const amount = variant === 'closed' ? trade.profit : trade.unrealizedPnl;
        const percent =
            variant === 'closed'
                ? trade.profitPercentage
                : trade.unrealizedPnl !== null && trade.openingValue > 0
                  ? (trade.unrealizedPnl / trade.openingValue) * 100
                  : null;

        return (
            <div
                className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2.5 gap-y-1 border-t border-zinc-800 py-2 text-sm first:border-t-0"
                key={trade.tradeId}
            >
                <StrategyColorDot color={strategyColors.get(trade.strategyId) ?? '#71717a'} />
                <div className="min-w-0">
                    <div className="flex flex-wrap items-center gap-x-2 font-medium text-white">
                        {entity ? <EntityLink showName={false} entity={entity} /> : <span>Entity {trade.entityId}</span>}
                        <span className="text-xs font-normal text-zinc-500">{option ? option.description : getInstrumentLabel(trade)}</span>
                    </div>
                    <div className="truncate text-xs text-zinc-500">
                        {strategyNames.get(trade.strategyId) ?? `Strategy ${trade.strategyId}`}
                        {variant === 'closed' && ` · ${getExitReasonLabel(trade.exitReason)} · held ${getTradeHoldDays(trade, today)}d`}
                        {variant === 'opened' && ` · cost ${formatUnsignedCurrency(trade.openingValue, 0)}`}
                        {variant === 'held' && ` · opened ${formatShortDate(trade.entryDate)}`}
                        {' · '}
                        <Link className="text-blue-400 hover:text-blue-300" to={`/user/visualizeTrade/${trade.tradeId}`}>
                            chart
                        </Link>
                    </div>
                </div>
                <div className={`text-right font-semibold ${signedToneClass(amount)}`}>
                    {variant === 'opened' ? (
                        <span className="text-xs font-normal text-zinc-500">opened</span>
                    ) : (
                        <>
                            {formatSignedCurrency(amount, variant === 'closed' ? 2 : 0)}
                            <div className="text-xs font-normal text-zinc-500">
                                {amount === null ? 'no mark' : formatSignedPercent(percent, 1)}
                            </div>
                        </>
                    )}
                </div>
            </div>
        );
    };

    const change = detail ? getDayPnl(detail.day, basis, scope) : 0;
    const strategyDays = detail ? getScopedStrategyDays(detail.day, scope) : [];
    const showStrategyBreakdown = detail !== null && (scope === null || scope.size > 1) && strategyDays.length > 0;

    return (
        <Drawer
            footer={
                <div className="flex flex-wrap gap-4 text-xs">
                    <Link className="text-blue-400 hover:text-blue-300" to={`/user/portfolioTradeHistory/${portfolioId}`}>
                        Trade history
                    </Link>
                    <Link className="text-blue-400 hover:text-blue-300" to={`/user/entryLog/?portfolioId=${portfolioId}`}>
                        Entry log
                    </Link>
                </div>
            }
            isOpen={isOpen}
            title={detail ? formatLongDate(detail.day.date) : 'Day'}
            width="md"
            onClose={onClose}
        >
            {detail && (
                <div className="space-y-5">
                    <section>
                        <SectionTitle>{scope === null ? 'Equity' : 'Scoped P&L'}</SectionTitle>
                        <div className="space-y-1">
                            {scope === null && (
                                <>
                                    <Row label="Start of day" value={formatUnsignedCurrency(detail.day.startingEquity)} />
                                    <Row label="End of day" value={formatUnsignedCurrency(detail.day.endingEquity)} />
                                    {detail.day.fundingFlow !== 0 && (
                                        <Row
                                            label="Funding"
                                            value={<span className="text-blue-400">{formatSignedCurrency(detail.day.fundingFlow)}</span>}
                                        />
                                    )}
                                </>
                            )}
                            <Row
                                emphasized
                                label="Change"
                                value={
                                    <span className={signedToneClass(change)}>
                                        {formatSignedCurrency(change)}
                                        {scope === null &&
                                            detail.day.startingEquity > 0 &&
                                            ` (${formatSignedPercent((change / detail.day.startingEquity) * 100)})`}
                                    </span>
                                }
                            />
                            {scope === null && (
                                <>
                                    <Row
                                        label="Realized closes"
                                        value={
                                            <span className={signedToneClass(detail.day.realizedPnl)}>
                                                {formatSignedCurrency(detail.day.realizedPnl)}
                                            </span>
                                        }
                                    />
                                    <Row
                                        label="Open-position marks"
                                        value={
                                            <span className={signedToneClass(detail.day.markToMarketPnl)}>
                                                {formatSignedCurrency(detail.day.markToMarketPnl)}
                                            </span>
                                        }
                                    />
                                    {Math.abs(detail.day.unattributedPnl) >= 0.01 && (
                                        <Row
                                            label="Unattributed (fees, dividends, rounding)"
                                            value={formatSignedCurrency(detail.day.unattributedPnl)}
                                        />
                                    )}
                                </>
                            )}
                        </div>
                    </section>

                    {showStrategyBreakdown && (
                        <section>
                            <SectionTitle>By strategy</SectionTitle>
                            <div className="space-y-1">
                                {strategyDays.map((strategyDay) => {
                                    const total = Math.round((strategyDay.realizedPnl + strategyDay.markToMarketPnl) * 100) / 100;

                                    return (
                                        <div className="flex items-center justify-between gap-3 text-sm" key={strategyDay.strategyId}>
                                            <span className="flex min-w-0 items-center gap-2 text-zinc-300">
                                                <StrategyColorDot color={strategyColors.get(strategyDay.strategyId) ?? '#71717a'} />
                                                <span className="truncate">
                                                    {strategyNames.get(strategyDay.strategyId) ?? `Strategy ${strategyDay.strategyId}`}
                                                </span>
                                            </span>
                                            <span className="whitespace-nowrap text-right">
                                                <span className={`font-semibold ${signedToneClass(total)}`}>
                                                    {formatSignedCurrency(total)}
                                                </span>
                                                <span className="ml-2 text-xs text-zinc-500">
                                                    {formatSignedCurrency(strategyDay.realizedPnl, 0)} realized ·{' '}
                                                    {formatSignedCurrency(strategyDay.markToMarketPnl, 0)} marks
                                                </span>
                                            </span>
                                        </div>
                                    );
                                })}
                            </div>
                        </section>
                    )}

                    <section>
                        <SectionTitle>Closed ({detail.closed.length})</SectionTitle>
                        {detail.closed.length ? (
                            detail.closed.map((trade) => renderTrade(trade, 'closed'))
                        ) : (
                            <div className="text-sm text-zinc-500">No trades closed.</div>
                        )}
                    </section>

                    <section>
                        <SectionTitle>Opened ({detail.opened.length})</SectionTitle>
                        {detail.opened.length ? (
                            detail.opened.map((trade) => renderTrade(trade, 'opened'))
                        ) : (
                            <div className="text-sm text-zinc-500">No trades opened.</div>
                        )}
                    </section>

                    <section>
                        <SectionTitle>Held at close ({detail.held.length})</SectionTitle>
                        {detail.held.length ? (
                            detail.held.map((trade) => renderTrade(trade, 'held'))
                        ) : (
                            <div className="text-sm text-zinc-500">Flat.</div>
                        )}
                    </section>
                </div>
            )}
        </Drawer>
    );
};

export default DayDetailDrawer;
